import { coreEngine } from "./engine.js";
import { nodes } from "./nodes.js";
import { time } from "./time.js";
import { createSingletonLogger } from "./singleton-logger.js";

class Bridge {
  // Component identity for logging
  static componentName = "bridge";

  #logger;
  _runtimes = new Map();
  _timeConfigs = new Map();

  instances = {};

  constructor() {
    // Initialize the singleton logger
    this.#logger = createSingletonLogger(this);
  }

  register(runtime) {
    runtime._bridge = this;
    this._timeConfigs.set(runtime.name, { timeOffset: 0, timeScale: 1 });
    for (const [prefix, engine] of runtime.engines) {
      this._runtimes.set(prefix, engine);
    }
  }

  registerPrefix(prefix, engine) {
    this._runtimes.set(prefix, engine);
  }

  deregister(name) {
    this._runtimes.delete(name);
  }

  setTimeConfig(name, { timeOffset, timeScale }) {
    this._timeConfigs.set(name, { timeOffset, timeScale });
  }

  connect(name, config) {
    if (!config || config.address) {
      // Remote transport — not handled yet
      return;
    }
    // In-process: engine already registered via register()
    // This method exists for the config-driven bootstrap path
    const runtime = this._runtimes.get(name);
    if (!runtime) {
      this.#logger.warn(`connect: no engine registered for runtime "${name}"`);
    }
  }

  disconnect(name) {
    this._runtimes.delete(name);
  }

  create(message) {
    const { uid } = message;
    const slashIndex = uid.indexOf("/");
    const runtimeName = uid.slice(0, slashIndex);
    const strippedUid = uid.slice(slashIndex + 1);
    const runtime = this._runtimes.get(runtimeName);
    if (runtime) {
      const outMsg = { ...message, uid: strippedUid };
      this.#applyOutboundTimeConversion(outMsg, runtimeName);
      runtime.create(outMsg);
    } else {
      this.#logger.warn("runtime not found", runtimeName, message);
    }
  }

  update(message) {
    const { uid } = message;
    const slashIndex = uid.indexOf("/");

    if (slashIndex === -1) {
      // Inbound: environment → core
      const { key, value, sender } = message;

      let messageTime = time.current;
      if (message.time !== undefined && sender) {
        const timeConfig = this._timeConfigs.get(sender);
        if (timeConfig && (timeConfig.timeOffset !== 0 || timeConfig.timeScale !== 1)) {
          messageTime = message.time / timeConfig.timeScale + timeConfig.timeOffset;
        }
      }

      const cNode = nodes.get(uid);
      if (cNode) {
        cNode.coreImplementation.update(cNode, { uid, key, value, sender, time: messageTime, _envMessage: true });
      }
      return;
    }

    // Outbound: core → environment
    const runtimeName = uid.slice(0, slashIndex);
    const runtime = this._runtimes.get(runtimeName);
    if (runtime) {
      const outMsg = { ...message, uid: uid.slice(slashIndex + 1) };
      this.#applyOutboundTimeConversion(outMsg, runtimeName);
      runtime.update(outMsg);
    } else {
      this.#logger.warn("runtime not found", runtimeName, message);
    }
  }

  destroy(message) {
    const { uid } = message;
    const slashIndex = uid.indexOf("/");
    const runtimeName = uid.slice(0, slashIndex);
    const strippedUid = uid.slice(slashIndex + 1);
    const runtime = this._runtimes.get(runtimeName);
    if (runtime) {
      const outMsg = { ...message, uid: strippedUid };
      this.#applyOutboundTimeConversion(outMsg, runtimeName);
      runtime.destroy(outMsg);
    } else {
      this.#logger.warn("runtime not found", runtimeName, message);
    }
  }

  #applyOutboundTimeConversion(message, runtimeName) {
    if (message.time === undefined) {
      return;
    }
    const timeConfig = this._timeConfigs.get(runtimeName);
    if (timeConfig && (timeConfig.timeOffset !== 0 || timeConfig.timeScale !== 1)) {
      message.time = (message.time - timeConfig.timeOffset) * timeConfig.timeScale;
    }
  }
}

const bridge = new Bridge();
export { Bridge, bridge };